/**
 * tourbookLayout.ts — layout model for the Bandz Tour Book PDF generator.
 *
 * The Tour Book PDF (buildTourBookDoc in src/lib/tourbook.ts) is assembled from
 * a small set of high-level sections that are specific to the tour book (as
 * opposed to the per-show daysheet, which has its own layout). This module lets
 * an admin toggle/reorder these sections and edit fixed texts & style.
 *
 * - createDefaultTourBookLayout() reproduces the current hard-coded behaviour.
 * - mergeTourBookLayouts() layers a persisted override over the defaults.
 *
 * Persisted in the `pdf_layouts` table under 'tourbook:global' + 'tourbook:<id>'.
 */

/** The top-level sections of the Tour Book PDF, in the current render order. */
export type TourBookSectionId =
  | 'intro'              // introduction page (band + tour + dates)
  | 'technical-overview' // TOUR TECHNICAL OVERVIEW table
  | 'daysheets'          // each show's daysheet in chronological order
  | 'next-note';         // "NEXT: <date>" note at the end of each daysheet

/** One section of the tour book: enabled flag + optional title override. */
export interface TourBookLayoutSection {
  id: TourBookSectionId;
  enabled: boolean;
  /** When a non-empty string, replaces the default heading / subtitle text. */
  title?: string | null;
  /** Start the section on a new page. */
  pageBreakBefore?: boolean;
  /**
   * Ordered list of the section's renderable fields. When absent the section
   * renders ALL its fields in the engine's default order. When present, only
   * enabled fields render, in list order. Spacer ('spacer') and free-text
   * ('text') fields are supported, as in the rider/daysheet editors.
   */
  fields?: TourBookLayoutField[];
  /**
   * Largeurs ÉDITABLES des colonnes du tableau de la section, par id de colonne
   * (poids relatif, normalisé en % au rendu). Absent = défauts du moteur.
   */
  columnWidths?: Record<string, number> | null;
}

/** A single renderable field inside a tour-book section. */
export interface TourBookLayoutField {
  /** Stable engine id, e.g. 'intro.band', 'spacer', 'text'. */
  id: string;
  enabled: boolean;
  /** Label shown in the editor (null = engine default label). */
  label?: string | null;
  /** Spacer fields only: vertical gap height in pdfmake units (default 8). */
  size?: number;
  /** Free-text fields only (id 'text'): the paragraph content. */
  textContent?: string;
  /** Free-text fields only: bold paragraph. */
  bold?: boolean;
  /** Free-text fields only: italic paragraph. */
  italic?: boolean;
  /** Table-column fields only (`<section>.<columnId>`): POIDS de la colonne. */
  width?: number;
  /** Free-text fields only: font size override. */
  fontSize?: number;
  /** Free-text fields only: horizontal alignment. */
  align?: 'left' | 'center' | 'right';
}

/** Fixed texts of the tour book. {token} placeholders are filled at render time. */
export interface TourBookLayoutTexts {
  /** Appended to the band name for the document title, e.g. 'TOUR BOOK'. */
  titleSuffix: string;
  /** Label of the next-date note (the {show} / {date} placeholders are added). */
  nextLabel: string;
  /** Footer left; {tourName} placeholder. */
  footerLeftTemplate: string;
  /** Footer center; {bandName} placeholder. */
  footerCenterTemplate: string;
  /** Footer right; {page} and {pages} placeholders. */
  footerRightTemplate: string;
  /** When true (default), the footer is printed at the bottom of EVERY page.
   * Optional for back-compat: absent = enabled. The footer only renders when at
   * least one of the three templates is filled in. */
  footerEnabled?: boolean;
  /** When true, a running header is printed at the top of EVERY page. */
  headerEnabled: boolean;
  headerLeftTemplate: string;
  headerCenterTemplate: string;
  headerRightTemplate: string;
}

/** Visual style of the tour book PDF. Values match the current generator. */
export interface TourBookLayoutStyle {
  titleFontSize: number;
  titleColor: string;
  subtitleFontSize: number;
  subtitleColor: string;
  headingFontSize: number;
  headingColor: string;
  bodyFontSize: number;
  textColor: string;
  footerFontSize: number;
  footerColor: string;
  pageMargins: [number, number, number, number];
}

/** Complete persisted layout for the Tour Book PDF generator. */
export interface TourBookLayout {
  version: 1;
  sections: TourBookLayoutSection[];
  texts: TourBookLayoutTexts;
  style: TourBookLayoutStyle;
}

/** Section ids in the current hard-coded render order (default layout). */
export const DEFAULT_TOURBOOK_SECTION_ORDER: TourBookSectionId[] = [
  'intro',
  'technical-overview',
  'daysheets',
  'next-note',
];

/** Friendly editor names for each section. */
export const DEFAULT_TOURBOOK_SECTION_TITLES: Record<TourBookSectionId, string> = {
  intro: 'Introduction page',
  'technical-overview': 'Technical overview',
  daysheets: 'Daysheets (chronological)',
  'next-note': 'Next-date note',
};

/** What each section renders — shown in the editor. */
export const TOURBOOK_SECTION_DETAILS: Record<TourBookSectionId, string[]> = {
  intro: [
    'Band name + “TOUR BOOK” title',
    'Tour name',
    'Dates / First date / Last date',
  ],
  'technical-overview': [
    'TOUR TECHNICAL OVERVIEW table: Date | Show | Stage | Length',
  ],
  daysheets: [
    'Each show’s daysheet, in chronological order (page break before each)',
  ],
  'next-note': [
    '“NEXT: <next show> — <date>” at the end of each daysheet (except the last)',
  ],
};

/** Default fixed texts — identical to the current hard-coded generator. */
export const DEFAULT_TOURBOOK_LAYOUT_TEXTS: TourBookLayoutTexts = {
  titleSuffix: 'TOUR BOOK',
  nextLabel: 'NEXT',
  footerLeftTemplate: '',
  footerCenterTemplate: '',
  footerRightTemplate: '',
  headerEnabled: false,
  headerLeftTemplate: '',
  headerCenterTemplate: '',
  headerRightTemplate: '',
};

/** Default style — identical to the current hard-coded generator. */
export const DEFAULT_TOURBOOK_LAYOUT_STYLE: TourBookLayoutStyle = {
  titleFontSize: 24,
  titleColor: '#000000',
  subtitleFontSize: 18,
  subtitleColor: '#444444',
  headingFontSize: 14,
  headingColor: '#000000',
  bodyFontSize: 10,
  textColor: '#000000',
  footerFontSize: 8,
  footerColor: '#888888',
  pageMargins: [40, 40, 40, 50],
};

/** Default renderable fields per section (intro + technical-overview granular;
 * daysheets is a single unit; next-note has the note label). */
export const DEFAULT_TOURBOOK_SECTION_FIELDS: Record<TourBookSectionId, { id: string; label: string }[]> = {
  intro: [
    { id: 'intro.band', label: 'Band name + title' },
    { id: 'intro.tour', label: 'Tour name' },
    { id: 'intro.notes', label: 'Tour notes' },
    { id: 'intro.dates', label: 'Dates / First / Last' },
  ],
  // La table du technical overview n'a plus d'entrée bloc : ses colonnes sont
  // des champs (`technical-overview.<columnId>`), comme dans le rider.
  'technical-overview': [
    { id: 'technical-overview.heading', label: 'Overview heading' },
  ],
  daysheets: [{ id: 'daysheets.all', label: 'All daysheets (chronological)' }],
  'next-note': [{ id: 'next-note.label', label: 'Next-date note' }],
};

/** The default layout = EXACT current behaviour of buildTourBookDoc. */
export function createDefaultTourBookLayout(): TourBookLayout {
  return {
    version: 1,
    sections: DEFAULT_TOURBOOK_SECTION_ORDER.map((id) => ({
      id,
      enabled: true,
      title: null,
      fields: tourbookTableColumnFields(id).length
        ? [...(DEFAULT_TOURBOOK_SECTION_FIELDS[id] || []).map((f) => ({ id: f.id, enabled: true, label: f.label })), ...tourbookTableColumnFields(id)]
        : (DEFAULT_TOURBOOK_SECTION_FIELDS[id] || []).map((f) => ({ id: f.id, enabled: true, label: f.label })),
    })),
    texts: { ...DEFAULT_TOURBOOK_LAYOUT_TEXTS },
    style: { ...DEFAULT_TOURBOOK_LAYOUT_STYLE },
  };
}

/**
 * Merge a persisted (partial) layout override over the defaults.
 * - texts/style: shallow-merged per key (override wins, defaults fill gaps).
 * - sections: the override array wins WHOLESALE (it is the full ordered list);
 *   when absent the default sections are kept. Section fields are backfilled
 *   with any new default fields missing from the saved list.
 */
export function mergeTourBookLayouts(
  base: TourBookLayout,
  override: Partial<TourBookLayout> | null,
): TourBookLayout {
  if (!override) return base;
  const defaultFieldsFor = (id: TourBookSectionId) => {
    const cols = tourbookTableColumnFields(id);
    if (cols.length) return cols; // section TABLEAU : une entrée par colonne
    return (DEFAULT_TOURBOOK_SECTION_FIELDS[id] || []).map((f) => ({ id: f.id, enabled: true, label: f.label }));
  };
  const sections = (override.sections ?? base.sections).map((s) => {
    const sid = s.id as TourBookSectionId;
    const isTable = (TOURBOOK_TABLE_COLUMNS[sid] || []).length > 0;
    const rawSaved = Array.isArray(s.fields) && s.fields.length > 0 ? s.fields : null;
    // Legacy : `<section>.table` devient une ligne morte (les colonnes prennent le relais).
    const savedFields = rawSaved
      ? rawSaved.filter((f) => !(isTable && f.id === `${sid}.table`))
      : null;
    const defaults = defaultFieldsFor(sid);
    const fields = savedFields
      ? [...savedFields, ...defaults.filter((d) => !savedFields.some((f) => f.id === d.id))]
      : defaults;
    return { ...s, fields };
  });
  return {
    version: override.version ?? base.version,
    sections,
    texts: { ...base.texts, ...(override.texts ?? {}) },
    style: { ...base.style, ...(override.style ?? {}) },
  };
}

/**
 * Colonnes des TABLEAUX du tour book : id stable, clé i18n du libellé, POIDS par
 * défaut (reproduit les largeurs historiques). Utilisé par l'éditeur ET le moteur.
 */
export interface TourBookTableColumn {
  id: string;
  labelKey: string;
  weight: number;
}

export const TOURBOOK_TABLE_COLUMNS: Partial<Record<TourBookSectionId, TourBookTableColumn[]>> = {
  // anciennes largeurs : ['50%', '50%']
  // Table « clé → valeur » : les libellés sont génériques (ce n'est pas la ligne
  // « Dates » en particulier — les deux colonnes valent pour TOUTES les lignes).
  intro: [
    { id: 'label', labelKey: 'layoutEditor.colLabel', weight: 50 },
    { id: 'value', labelKey: 'layoutEditor.colValue', weight: 50 },
  ],
  // ['26%', '*', '22%', '12%']
  'technical-overview': [
    { id: 'date', labelKey: 'pdf.tb.col.date', weight: 26 },
    { id: 'show', labelKey: 'pdf.tb.col.show', weight: 40 },
    { id: 'stage', labelKey: 'pdf.tb.col.stage', weight: 22 },
    { id: 'length', labelKey: 'pdf.tb.col.length', weight: 12 },
  ],
};

/** Champs par défaut des sections TABLEAU du tour book : une entrée par colonne. */
export function tourbookTableColumnFields(sectionId: TourBookSectionId): TourBookLayoutField[] {
  return (TOURBOOK_TABLE_COLUMNS[sectionId] || []).map((c) => ({
    id: `${sectionId}.${c.id}`,
    enabled: true,
    label: null,
    width: c.weight,
  }));
}

/**
 * Colonnes retenues d'une section tableau, dans l'ordre du layout.
 * Legacy (aucun id de colonne connu) → toutes les colonnes, ordre par défaut.
 */
export function tourbookTableColumns(
  section: TourBookLayoutSection | undefined,
  sectionId: TourBookSectionId,
): { id: string; labelKey: string; weight: number; labelOverride: string | null }[] {
  const defs = TOURBOOK_TABLE_COLUMNS[sectionId] || [];
  if (!defs.length) return [];
  const byId = new Map(defs.map((d) => [d.id, d]));
  const saved = (section?.fields ?? []).filter((f) => byId.has(f.id.slice(sectionId.length + 1)));
  const list: TourBookLayoutField[] = saved.length
    ? saved
    : defs.map((d) => ({ id: `${sectionId}.${d.id}`, enabled: true, label: null, width: d.weight }));
  return list
    .filter((f) => f.enabled !== false)
    .map((f) => {
      const cid = f.id.slice(sectionId.length + 1);
      const def = byId.get(cid)!;
      const w = typeof f.width === 'number' && f.width > 0 ? f.width : def.weight;
      const override = typeof f.label === 'string' && f.label.trim() ? f.label.trim() : null;
      return { id: cid, labelKey: def.labelKey, weight: w, labelOverride: override };
    });
}

/** Poids effectifs d'une section : surcharge du layout sinon défauts. */
export function tourbookColumnWeights(
  sectionId: TourBookSectionId,
  override?: Record<string, number> | null,
): number[] {
  const cols = TOURBOOK_TABLE_COLUMNS[sectionId] || [];
  return cols.map((c) => {
    const v = override?.[c.id];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : c.weight;
  });
}

/** `widths[]` pdfmake d'une section : poids normalisés en pourcentages. */
export function tourbookTableWidths(
  sectionId: TourBookSectionId,
  override?: Record<string, number> | null,
): string[] {
  const weights = tourbookColumnWeights(sectionId, override);
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => `${((w / total) * 100).toFixed(2)}%`);
}
